"use client";

import {
  addSavedArticleAction,
  deleteSavedArticlesAction,
  getSavedArticlesAction,
  getTopicsAction,
  getUserFromTokenAction,
  logoutAction,
  updateTopicsAction,
} from "@/app/actions";
import { EfnAuth } from "@/services/auth/types";
import { useRouter } from "next/navigation";
import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from "react";

type UserContextType = {
  toggleTopic: (props: EfnAuth.Topic) => void;
  toggleSaveForLater: (id: string) => void;
  getUser: () => void;
  logout: (callback?: () => void) => void;
  user: UserType | null;
  isAuthenticated: boolean;
  savedArticles?: string[];
};

type UserType = {
  topics: EfnAuth.Topic[];
  info: EfnAuth.User;
  products: string[];
};

export const UserContext = createContext<UserContextType>({
  toggleTopic: () => null,
  toggleSaveForLater: () => null,
  getUser: () => null,
  logout: (callback?: () => void) => null,
  user: null,
  isAuthenticated: false,
});

export const UserContextProvider = ({ children }: PropsWithChildren) => {
  const [user, setUser] = useState<UserType | null>(null);
  const [savedArticles, setSavedArticles] = useState<string[]>([]);
  const router = useRouter();

  useEffect(() => {
    getUserData();
  }, []);

  useEffect(() => {
    if(user) {
      getSavedArticlesAction().then((articles) => {
        if (articles) {
          setSavedArticles(articles);
        }
      });
    }
  }, [user]);

  const logout = async (callback?: () => void) => {
    await logoutAction();
    router.refresh();
    router.push("/");
    if (callback) callback();
    setUser(null);
  };

  const getUserData = async () => {
    const user = await getUserFromTokenAction();
    const topics = await getTopicsAction();

    if (!user) return;

    const mergedData = {
      ...user,
      topics: topics ? topics : [],
    };
    setUser(mergedData);
  };

  const toggleTopic = async ({ type, id, label }: EfnAuth.Topic) => {
    const topics = await getTopicsAction();

    if (!topics || !user) return;

    const alreadyExists = topics.find(
      (sub: any) => sub?.id === id && sub?.type === type
    );

    if (alreadyExists) {
      const updatedTopics = topics.filter(
        (sub: any) => !(sub?.id == id && sub?.type == type)
      );

      await updateTopicsAction(updatedTopics);
      setUser({ ...user, topics: updatedTopics });

      window.parent.postMessage(
        {
          name: "remove-topic",
          data: { id, type, ...(label ? { label } : {}) },
        },
        "*"
      );

      return;
    }

    const updatedTopics = [
      ...topics,
      { id, type, ...(label ? { label } : {}) },
    ];
    await updateTopicsAction(updatedTopics);
    setUser({ ...user, topics: updatedTopics });

    window.parent.postMessage(
      {
        name: "add-topic",
        data: { id, type, ...(label ? { label } : {}) },
      },
      "*"
    );
  };
  
  
  const toggleSaveForLater = async (id: string) => {

    if (!savedArticles || !user) return;

    const alreadyExists = savedArticles.find(
      (savedArticle: any) => savedArticle === id
    );

    if (alreadyExists) {
      const updatedSavedArticles = savedArticles.filter(
        (savedArticle: any) => savedArticle !== id
      );

      await deleteSavedArticlesAction(id); 
      setSavedArticles(updatedSavedArticles);

      window.parent.postMessage(
        {
          name: "remove-saved-article",
          data: id
        },
        "*"
      );

      return;
    }

    const updatedSavedArticles = [
      ...savedArticles,
      id,
    ];
    await addSavedArticleAction(id);
    setSavedArticles(updatedSavedArticles);

    window.parent.postMessage(
      {
        name: "add-saved-article",
        data: id,
      },
      "*"
    );
  };
  
  return (
    <UserContext.Provider
      value={{
        user,
        getUser: getUserData,
        toggleTopic,
        toggleSaveForLater,
        logout,
        isAuthenticated: Boolean(user),
        savedArticles
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);
